import * as React from "react";
import { NavBarButtonWrapper } from "./nav-bar-style";
import { MenuItem } from "../../../lib/menu-item";
import { defined } from "common/Support";
import { RuntimeTheme } from "../../../lib/theme/theme";
import { definedMap } from "@glideapps/ts-necessities";
import AppIcon from "common/app-icon";
import { MenuItemPurpose } from "common/components/types";
import useNetworkStatus from "hooks/use-network-status";

interface Props {
    readonly theme: RuntimeTheme;
    readonly menuItem: MenuItem;
    readonly offlineAddRow: boolean;
}

export function NavBarButton(p: Props) {
    const { theme, menuItem, offlineAddRow } = p;

    const shouldUseText = React.useCallback(
        function (): boolean {
            const style = menuItem.style;
            const isAndroid = theme.navBar.style === "Android";
            const isIOS = !isAndroid;

            if (
                style === undefined ||
                style === "share" ||
                style === "icon" ||
                style === "default" ||
                style === "menu"
            ) {
                return false;
            }

            if (style === "text") {
                return true;
            }

            if (isIOS) {
                return true;
            } else {
                return false;
            }
        },
        [menuItem.style, theme.navBar.style]
    );

    // These do something online
    const needsOnline =
        menuItem.purpose === MenuItemPurpose.AddRow ||
        menuItem.purpose === MenuItemPurpose.EditRow ||
        menuItem.purpose === MenuItemPurpose.SaveRow;
    // This one does something online, but can potentially work offline
    const canWorkOffline = menuItem.purpose === MenuItemPurpose.AddRow;
    const isOnline = useNetworkStatus();
    const enabledViaNetwork = isOnline || !needsOnline || (canWorkOffline && offlineAddRow);

    let content: React.ReactNode;
    const hasText = shouldUseText();
    let bold = false;
    let size = 24;

    if (!menuItem.icon.startsWith("00")) {
        size = 20;
    }

    if (hasText) {
        content = menuItem.title;
        if (menuItem.style === "platform-accept" || menuItem.style === "platform-submit") {
            bold = true;
        }
    } else if (menuItem.style === "platform-cancel") {
        content = <AppIcon icon={menuItem.icon} size={size} color={defined(theme).navBar.cancelColor} />;
    } else {
        content = <AppIcon icon={menuItem.icon} size={size} />;
    }

    const isEnabled = menuItem.isEnabled !== false && enabledViaNetwork;

    return (
        <NavBarButtonWrapper
            setFill={false}
            bold={bold}
            data-test={"nav-button-" + menuItem.title}
            aria-label={menuItem.title}
            enabled={isEnabled}
            onClick={definedMap(menuItem.onClick, x => () => x(false))}>
            {content}
        </NavBarButtonWrapper>
    );
}

export default NavBarButton;
